import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Shield, CheckCircle, XCircle, ExternalLink, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LicenseVerificationManager = () => {
  const [pendingVerifications, setPendingVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [rejectionNotes, setRejectionNotes] = useState({});

  useEffect(() => {
    fetchPendingVerifications();
  }, []);

  const fetchPendingVerifications = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/pending-license-verifications`, {
        withCredentials: true
      });
      setPendingVerifications(response.data.pending_verifications || []);
    } catch (error) {
      console.error('Error fetching pending verifications:', error);
      toast.error('Failed to load pending verifications');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyLicense = async (userId, action, notes = '') => {
    try {
      setProcessing(userId);
      
      await axios.post(
        `${API}/admin/verify-license/${userId}`,
        { action, notes },
        { withCredentials: true }
      );

      toast.success(`License ${action}d successfully!`);
      
      // Refresh list
      fetchPendingVerifications();
      
      // Clear notes
      setRejectionNotes({ ...rejectionNotes, [userId]: '' });
    } catch (error) {
      console.error('Error verifying license:', error);
      toast.error(error.response?.data?.detail || 'Failed to verify license');
    } finally {
      setProcessing(null);
    }
  };

  const openTRECVerification = (licenseNumber) => {
    const trecUrl = `https://www.trec.texas.gov/apps/license-holder-search/?lic_name=${licenseNumber}`;
    window.open(trecUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-orange-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading pending verifications...</p>
      </div>
    );
  }

  if (pendingVerifications.length === 0) {
    return (
      <Card className="p-12 text-center">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-10 h-10 text-green-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">All Caught Up!</h3>
        <p className="text-gray-600">No pending license verifications at this time.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
            <Shield className="w-6 h-6 text-orange-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">License Verifications</h2>
            <p className="text-sm text-gray-500">
              {pendingVerifications.length} pending verification{pendingVerifications.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        <div className="space-y-4">
          {pendingVerifications.map((user) => (
            <Card key={user.id} className="p-6 border-2 border-orange-200 bg-orange-50">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className="text-xl font-bold text-gray-900">{user.name}</h3>
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full flex items-center">
                      <Clock className="w-3 h-3 mr-1" />
                      PENDING
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Email:</p>
                      <p className="font-semibold text-gray-900">{user.email}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">License #:</p>
                      <p className="font-semibold text-gray-900">{user.realtor_license_number}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Brokerage:</p>
                      <p className="font-semibold text-gray-900">{user.realtor_brokerage}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Brokerage Phone:</p>
                      <p className="font-semibold text-gray-900">{user.realtor_brokerage_phone || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Signed Up:</p>
                      <p className="font-semibold text-gray-900">
                        {new Date(user.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* TREC Verification Button */}
              <div className="mb-4">
                <Button
                  onClick={() => openTRECVerification(user.realtor_license_number)}
                  className="w-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center space-x-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span>Verify on TREC Website</span>
                </Button>
                <p className="text-xs text-gray-600 mt-2 text-center">
                  Opens Texas Real Estate Commission license search in new tab
                </p>
              </div>

              {/* Rejection Notes (shown only when user clicks reject) */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (optional - will be sent to realtor if rejected):
                </label>
                <textarea
                  value={rejectionNotes[user.id] || ''}
                  onChange={(e) => setRejectionNotes({ ...rejectionNotes, [user.id]: e.target.value })}
                  placeholder="E.g., License expired, name mismatch, etc."
                  className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  rows="3"
                />
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-4">
                <Button
                  onClick={() => handleVerifyLicense(user.id, 'approve')}
                  disabled={processing === user.id}
                  className="bg-green-600 hover:bg-green-700 flex items-center justify-center space-x-2"
                >
                  <CheckCircle className="w-4 h-4" />
                  <span>{processing === user.id ? 'Processing...' : 'Approve License'}</span>
                </Button>
                
                <Button
                  onClick={() => handleVerifyLicense(user.id, 'reject', rejectionNotes[user.id])}
                  disabled={processing === user.id}
                  variant="destructive"
                  className="flex items-center justify-center space-x-2"
                >
                  <XCircle className="w-4 h-4" />
                  <span>{processing === user.id ? 'Processing...' : 'Reject License'}</span>
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default LicenseVerificationManager;
